import { json } from "@remix-run/node";
import { Link, useLoaderData, useParams } from "@remix-run/react";
import { formatDate } from "date-fns";
import { ArrowLeft, ImageOff, Newspaper } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useNews } from "~/api/queries";
import { Icon } from "~/components/Icon";
import { JsonLd } from "~/components/JsonLd";
import Viewer from "~/components/RichText/Viever";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { useLanguageStore } from "~/modules/Dashboard/Settings/Language/LanguageStore";
import {
  buildMeta,
  getCompanyFromMatches,
  truncateForMeta,
  ogImageUrl,
} from "~/utils/meta-helpers";
import { serverFetchOutcome } from "~/utils/server-fetch.server";

import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import type { GetNewsResponse } from "~/api/generated-api";

/**
 * Loader de SERVIDOR. Reemplaza al placeholder que devolvía `{ news: null }` — que además de
 * dejar el `meta` en genérico era lo que cortaba el SSR de esta ruta entera: con
 * `clientLoader` y sin `loader`, Remix borra la `loaderData` y React Router sube al
 * `HydrateFallback` de root. Con la data sembrada salen el cuerpo Y el JSON-LD `NewsArticle`
 * dentro del HTML, que es lo único que ve un crawler de LLM.
 *
 * Mismo endpoint que ya usa el alternate `/news/:id.md` de server.js.
 *
 * OJO — BLOQUEO DE DATOS, no de código: hoy `/api/news/:id` devuelve 400
 * `common.toast.noAccess` al anónimo (`news.service.ts#checkAccess` contra los settings
 * globales). Mientras siga apagado, esto sirve 404 — exactamente lo mismo que ya hace hoy
 * el `.md` gemelo. El usuario logueado sigue viendo la noticia: React Query la resuelve al
 * hidratar, y el status del documento no frena el render.
 */
export const loader = async ({ params }: LoaderFunctionArgs) => {
  const newsId = params.newsId || "";

  const empty = { news: null as GetNewsResponse["data"] | null };

  if (!newsId) return json(empty, { status: 404 });

  const outcome = await serverFetchOutcome<GetNewsResponse>(
    `/api/news/${encodeURIComponent(newsId)}?language=es`,
  );

  // 404 REAL, no un soft-404. `json(..., { status })` renderiza el componente igual (React
  // Router toma el status más profundo distinto de 200), así que el humano ve la misma
  // pantalla de "no encontrada" de siempre y el bot recibe el status correcto.
  //
  // Y 503 —no 404— cuando la API no contestó: un timeout no puede significar "esta noticia
  // dejó de existir, sacala del índice". Ver `serverFetchOutcome`.
  if (!outcome.ok) {
    return json(empty, { status: outcome.reason === "not-found" ? 404 : 503 });
  }

  return json({ news: outcome.data.data ?? null });
};

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const company = getCompanyFromMatches(matches);
  const news = data?.news;

  if (!news) {
    return buildMeta({ title: `Noticias | ${company}` });
  }

  return buildMeta({
    title: `${news.title} | ${company}`,
    description: truncateForMeta(news.summary || news.content),
    image: ogImageUrl(news.resources?.coverImage?.fileUrl),
    type: "article",
  });
};

export default function LandingNewsDetailPage() {
  const { t } = useTranslation();
  const { newsId } = useParams();
  const { language } = useLanguageStore();

  // Siembra del loader. `fetchedNews` gana apenas resuelve: es la que ve el estado del
  // usuario logueado, porque el fetch del servidor es anónimo a propósito.
  const { news: ssrNews } = useLoaderData<typeof loader>();
  const { data: fetchedNews, isLoading: isFetching } = useNews(
    newsId!,
    { language },
    { enabled: Boolean(newsId) },
  );
  const news = fetchedNews ?? ssrNews ?? undefined;

  // El skeleton sólo si NO hay nada que mostrar.
  if (isFetching && !news) {
    return <NewsDetailSkeleton />;
  }

  if (!news) {
    return (
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-neutral-100 p-4">
              <Newspaper className="size-8 text-neutral-400" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-neutral-900">
            {t("landing.newsDetail.notFound")}
          </h1>
          <Button asChild className="mt-6">
            <Link to="/news">{t("landing.newsDetail.backToNews")}</Link>
          </Button>
        </div>
      </section>
    );
  }

  const headerImageUrl = news.resources?.coverImage?.fileUrl;
  const publishedDate = news.publishedAt ? new Date(news.publishedAt) : null;

  return (
    <section className="py-12 sm:py-16">
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: news.title,
          description: news.summary || "",
          ...(headerImageUrl && { image: headerImageUrl }),
          publisher: { "@type": "Organization", name: "AcademIA" },
        }}
      />
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Back link */}
        <Link
          to="/news"
          className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-landing-red mb-8"
        >
          <ArrowLeft className="size-4" />
          {t("landing.newsDetail.backToNews")}
        </Link>

        {/* Article Container */}
        <article className="overflow-hidden rounded-2xl border border-neutral-200 bg-landing-white shadow-sm">
          {/* Cover Image */}
          {headerImageUrl ? (
            <div className="aspect-video w-full overflow-hidden bg-neutral-100">
              <img src={headerImageUrl} alt={news.title} className="h-full w-full object-cover" />
            </div>
          ) : (
            <div className="aspect-video w-full overflow-hidden bg-neutral-100 flex items-center justify-center">
              <ImageOff className="size-16 text-neutral-300" />
            </div>
          )}

          {/* Content */}
          <div className="p-6 sm:p-10">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-500 mb-4">
              {publishedDate && (
                <span className="flex items-center gap-1.5">
                  <Icon name="Calendar" className="size-4" />
                  {formatDate(publishedDate, "d MMMM yyyy")}
                </span>
              )}
              {news.authorName && (
                <span className="flex items-center gap-1.5">
                  <Icon name="User" className="size-4" />
                  {news.authorName}
                </span>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl mb-4">
              {news.title}
            </h1>

            {/* Summary */}
            {news.summary && (
              <p className="text-lg text-neutral-600 mb-8 leading-relaxed">{news.summary}</p>
            )}

            {/* Content */}
            {news.content && (
              <div className="border-t border-neutral-100 pt-8">
                <Viewer
                  content={news.content}
                  className="prose prose-neutral max-w-none"
                  variant="content"
                />
              </div>
            )}

            {/* Navigation */}
            <div className="mt-12 pt-8 border-t border-neutral-100 flex items-center justify-between">
              <Button
                variant="ghost"
                asChild={!!news.previousNews}
                disabled={!news.previousNews}
                className="flex items-center gap-2"
              >
                {news.previousNews ? (
                  <Link to={`/news/${news.previousNews}`}>
                    <Icon name="ChevronLeft" className="size-5" />
                    <span>{t("landing.newsDetail.previousNews")}</span>
                  </Link>
                ) : (
                  <>
                    <Icon name="ChevronLeft" className="size-5" />
                    <span>{t("landing.newsDetail.previousNews")}</span>
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                asChild={!!news.nextNews}
                disabled={!news.nextNews}
                className="flex items-center gap-2"
              >
                {news.nextNews ? (
                  <Link to={`/news/${news.nextNews}`}>
                    <span>{t("landing.newsDetail.nextNews")}</span>
                    <Icon name="ChevronRight" className="size-5" />
                  </Link>
                ) : (
                  <>
                    <span>{t("landing.newsDetail.nextNews")}</span>
                    <Icon name="ChevronRight" className="size-5" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </article>
      </div>
    </section>
  );
}

function NewsDetailSkeleton() {
  return (
    <section className="py-12 sm:py-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <Skeleton className="h-5 w-32 mb-8" />
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-landing-white shadow-sm">
          <Skeleton className="aspect-video w-full" />
          <div className="p-6 sm:p-10">
            <div className="flex gap-4 mb-4">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-24" />
            </div>
            <Skeleton className="h-10 w-3/4 mb-4" />
            <Skeleton className="h-6 w-full mb-2" />
            <Skeleton className="h-6 w-2/3 mb-8" />
            <div className="border-t border-neutral-100 pt-8">
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4 mb-4" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
